import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { i18nConfig } from '@glass/rock/modules/locales/config';

import { RESOURCE_POST_PATHNAME, RESOURCES_PATHNAME } from '@glass/common/modules/pages/paths';
import removeEmptyKeys from '@glass/common/modules/utils/removeEmptyKeys';
import { getLocaleOriginPath } from '@glass/env/modules/locales/getLocaleOrigin';
import { currentOrigin } from '@glass/env/modules/origins/constants';
import Container from '@glass/web/components/Layout/Container';
import SpinnerWithContainer from '@glass/web/components/Loading/SpinnerWithContainer';
import InlineCenterSkeleton from '@glass/web/components/Skeleton/InlineCenterSkeleton';
import Divider from '@glass/web/components/base/Divider';
import NoSsr from '@glass/web/components/base/NoSsr';
import Paper from '@glass/web/components/base/Paper';
import Typography from '@glass/web/components/base/Typography';
import { CENTER } from '@glass/web/modules/theme/constants';
import { COLUMN, FLEX, SPACE_BETWEEN } from '@glass/web/modules/theme/flexConstants';
import makeStyles from '@glass/web/modules/theme/makeStyles';

import withErrorPage from '@glass/shared/components/App/withErrorPage';
import AspectRatio from '@glass/shared/components/AspectRatio/AspectRatio';
import Breadcrumbs from '@glass/shared/components/Breadcrumbs/Breadcrumbs';
import EmphasisTitle from '@glass/shared/components/Content/EmphasisTitle';
import MarkdownContent from '@glass/shared/components/Content/MarkdownContent';
import ResourceLayoutPage from '@glass/shared/components/Content/ResourceLayoutPage';
import SocialShareLinks from '@glass/shared/components/Content/Social/SocialShareLinks';
import TagList from '@glass/shared/components/Content/TagList';
import blogPostPropTypes from '@glass/shared/components/Content/propTypes/blogPostPropTypes';
import DynamicRockErrorPage from '@glass/shared/components/Error/DynamicRockErrorPage';
import BreadcrumbStructuredData from '@glass/shared/components/Head/BreadcrumbStructuredData';
import NewsArticleStructuredData from '@glass/shared/components/Head/NewsArticleStructuredData';
import OgImageHead from '@glass/shared/components/Head/OgImageHead';
import ResponsiveImage from '@glass/shared/components/Image/ResponsiveImage';
import calcImageSizesV2 from '@glass/shared/components/Image/calcImageSizesV2';
import useSrc from '@glass/shared/components/Image/helpers/useSrc';
import Link from '@glass/shared/components/Link/Link';
import createContentfulClient from '@glass/shared/modules/apollo/createContentfulClient';
import blogTitle from '@glass/shared/modules/content/blogTitle';
import generateBlogPostDate from '@glass/shared/modules/content/helpers/generateBlogPostDate';
import blogPostCollectionQuery from '@glass/shared/modules/content/queries/blogPostCollection.graphql';
import blogPostCollectionDetailQuery from '@glass/shared/modules/content/queries/blogPostCollectionDetail.graphql';
import pageCollectionQuery from '@glass/shared/modules/content/queries/pageCollection.graphql';
import revalidateBlogDuration from '@glass/shared/modules/content/revalidateBlogDuration';
import ogImageSize from '@glass/shared/modules/images/ogImageSize';
import { pushResourcesInterest } from '@glass/shared/modules/interests/pushInterestAction';
import createGetStaticPropsWithErrorTracking from '@glass/shared/modules/next/createGetStaticPropsWithErrorTracking';
import getStaticParam from '@glass/shared/modules/next/getStaticParam';

const DEFAULT_SPACING = 2;

const useStyles = makeStyles()((theme) => ({
  authorName: {
    fontWeight: 700,
  },
  image: {
    height: '100%',
  },
  profileImage: {
    marginRight: theme.spacing(1),
    borderRadius: '50%',
  },
  my: {
    margin: theme.spacing(DEFAULT_SPACING, 0),
  },
  mt: {
    margin: theme.spacing(DEFAULT_SPACING, 0, 0, 0),
  },
  mb: {
    margin: theme.spacing(0, 0, DEFAULT_SPACING, 0),
  },
  subheader: {
    alignItems: CENTER,
    display: FLEX,
    justifyContent: SPACE_BETWEEN,
    marginBottom: theme.spacing(DEFAULT_SPACING),
  },
  authorContainer: {
    display: FLEX,
    alignItems: CENTER,
    flexShrink: 0,
    fontWeight: 700,
    paddingRight: theme.spacing(DEFAULT_SPACING),
  },
  titleContainer: {
    display: FLEX,
    flexDirection: COLUMN,
  },
}));

const imageSizes = calcImageSizesV2();

const generateReadTimeMinutes = ({ characters }) =>
  characters ? Math.floor(characters / 1800) + 1 : 1;

function BlogPostPage(props) {
  const { isFallback, locale } = useRouter();
  const { classes } = useStyles();
  const { canonicalPath, preview, blogPost, precedingBreadcrumbItems } = props;

  const {
    titleEmphasisIndex,
    title,
    author: {
      color: authorColor,
      name,
      image: { url: authorImageUrl, description: authorImageDescription } = {},
    } = {},
    heroImage: { url: heroUrl, description: heroDescription } = {},
    description,
    body,
    tagsCollection: { items: tags } = {},
    color,
    publishedAt,
    updatedAt,
  } = blogPost || {};

  const date = useMemo(
    () => generateBlogPostDate({ publishedAt, updatedAt }),
    [updatedAt, publishedAt],
  );
  const { length: characters } = body || {};
  const readTime = useMemo(() => generateReadTimeMinutes({ characters }), [characters]);

  const socialImageUrl = useSrc({
    width: ogImageSize.width,
    height: ogImageSize.height,
    external: true,
    path: heroUrl,
    origin: process.env.NEXT_PUBLIC_PRODUCTION_ORIGIN,
  });

  const dispatch = useDispatch();

  useEffect(() => {
    if (tags?.length) {
      dispatch(pushResourcesInterest(tags));
    }
  }, [dispatch, tags]);

  const shareUrl = useMemo(() => {
    return getLocaleOriginPath(locale, i18nConfig, currentOrigin) + canonicalPath;
  }, [locale, canonicalPath]);

  return (
    <ResourceLayoutPage
      canonicalPath={canonicalPath}
      color={color}
      description={description}
      pageTitle={title}
      preview={preview}
    >
      <OgImageHead alt={heroDescription} src={socialImageUrl} />
      {!isFallback ? (
        <Container isNoYPadding>
          <div className={classes.mt}>
            <BreadcrumbStructuredData items={precedingBreadcrumbItems} title={title} />
            <NewsArticleStructuredData
              authorName={name}
              heroImage={heroUrl}
              path={canonicalPath}
              publishedAt={publishedAt}
              title={title}
            />
            <Breadcrumbs
              disableStructuredData
              isPageTitle
              trailingSeparator
              items={precedingBreadcrumbItems}
              linkComponent={Link}
              variant="h5"
            />
            <EmphasisTitle
              gutterBottom
              color={color}
              componentColor="textPrimary"
              emphasisIndex={titleEmphasisIndex}
              title={title}
            />
            <div className={classes.subheader}>
              <div className={classes.authorContainer}>
                <ResponsiveImage
                  external
                  alt={authorImageDescription}
                  aspectRatio={1}
                  className={classes.profileImage}
                  height={50}
                  maxWidth={150}
                  path={authorImageUrl}
                  sizes="50px"
                  width={50}
                  widthStep={50}
                />
                <div className={classes.titleContainer}>
                  <EmphasisTitle color={authorColor} emphasisIndex={1} title={name} variant="h5" />
                  <Typography variant="subtitle2">
                    <NoSsr fallback={<InlineCenterSkeleton inline width={130} />}>{date}</NoSsr>{' '}
                    &bull; {readTime} min read
                  </Typography>
                </div>
              </div>
              <SocialShareLinks
                description={description}
                image={socialImageUrl}
                title={title}
                url={shareUrl}
              />
            </div>
            <div className={classes.mb}>
              <TagList disablePadding tags={tags} />
            </div>
          </div>
          <div className={classes.mb}>
            <Paper square variant="outlined">
              <AspectRatio height={1} width={1.9}>
                <ResponsiveImage
                  external
                  alt={heroDescription}
                  aspectRatio={1.9}
                  className={classes.image}
                  path={heroUrl}
                  sizes={imageSizes}
                />
              </AspectRatio>
            </Paper>
          </div>
          <div>
            <MarkdownContent>{body}</MarkdownContent>
            <div className={classes.my}>
              <Divider />
            </div>
            <div className={classes.my}>
              <TagList disablePadding tags={tags} />
            </div>
          </div>
        </Container>
      ) : (
        <SpinnerWithContainer />
      )}
    </ResourceLayoutPage>
  );
}

BlogPostPage.propTypes = {
  blogPost: blogPostPropTypes,
  canonicalPath: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  precedingBreadcrumbItems: PropTypes.array,
  preview: PropTypes.bool,
};

export default withErrorPage(DynamicRockErrorPage)(BlogPostPage);

export async function getStaticPaths() {
  const contentfulClient = createContentfulClient();
  const blogPostCollection = await contentfulClient
    .query({
      query: blogPostCollectionQuery,
      variables: {
        limit: 500,
        order: ['updatedAt_DESC', 'publishedAt_DESC'],
        relatedLimit: 0,
        tagLimit: 0,
      },
    })
    .catch(console.error);

  const paths =
    blogPostCollection?.data?.blogPostCollection?.items?.map(({ slug }) => ({
      params: { slug },
    })) || [];

  return {
    paths: process.env.PREVENT_STATIC_PATHS ? [] : paths,
    fallback: 'blocking',
  };
}

const handler = async ({ preview = false, params }) => {
  const slug = getStaticParam(params.slug, RESOURCE_POST_PATHNAME, '[slug]');

  const contentfulClient = createContentfulClient({ preview });
  const [pageCollection, blogPostCollection] = await Promise.all([
    contentfulClient
      .query({
        query: pageCollectionQuery,
        variables: {
          preview,
          where: { slug: '_blog' },
          limit: 1,
        },
      })
      .catch(console.error),
    contentfulClient
      .query({
        query: blogPostCollectionDetailQuery,
        variables: {
          preview,
          where: {
            slug,
          },
          limit: 1,
          relatedLimit: 8,
          tagLimit: 12,
        },
      })
      .catch(console.error),
  ]);

  const [blogPost] =
    blogPostCollection?.data?.blogPostCollection?.items.filter(
      ({ slug: contentSlug }) => contentSlug === slug,
    ) || [];

  if (!blogPost) {
    return {
      redirect: {
        destination: RESOURCES_PATHNAME,
        permanent: false,
      },
      revalidate: revalidateBlogDuration,
    };
  }

  const page = pageCollection?.data?.pageCollection.items?.[0];

  const canonicalPath = `${RESOURCES_PATHNAME}/${slug}`;

  return {
    props: removeEmptyKeys({
      preview,
      blogPost,
      slug,
      canonicalPath,
      precedingBreadcrumbItems: [
        removeEmptyKeys({
          href: RESOURCES_PATHNAME,
          text: page.title || page.pageTitle || blogTitle,
        }),
      ],
    }),
    revalidate: revalidateBlogDuration,
  };
};

export const getStaticProps = createGetStaticPropsWithErrorTracking(handler);
